import type {
  AppointmentAvailabilityRequest,
  AppointmentConflictCheckRequest,
  AppointmentListQuery,
  RecurrenceConflictRequest,
} from "@/api/appointments";

/**
 * Calendar Appointments v1.0 — query-key factory. Every appointment read
 * registers under this single family so all mutations can invalidate by the
 * <c>all</c> prefix (the stale-after-save gate requires queryKey array
 * literals to live ONLY in a *Keys file).
 */
export const appointmentKeys = {
  all: ["appointments"] as const,
  list: (range?: AppointmentListQuery) =>
    ["appointments", "list", range ?? {}] as const,
  detail: (id: string) => ["appointments", "detail", id] as const,
  upcoming: (limit: number) => ["appointments", "upcoming", limit] as const,
  /**
   * The entity Calendar tab's read. Sits under the same <c>appointments</c>
   * prefix as everything else, so the existing mutation invalidations
   * (which all invalidate <c>all</c>) already cover it — creating from an
   * entity page refreshes both the tab and the calendar grid with no extra
   * invalidation.
   */
  byRelated: (type: string, entityId: string) =>
    ["appointments", "by-related", type, entityId] as const,
  /** Informational conflict check, keyed on the (debounced) request payload. */
  conflictCheck: (input: AppointmentConflictCheckRequest | null) =>
    ["appointments", "conflict-check", input] as const,
  /** Per-day attendee availability, keyed on the (debounced) request payload. */
  availability: (input: AppointmentAvailabilityRequest | null) =>
    ["appointments", "availability", input] as const,
  /** Bounded busy check across a proposed rule's next few occurrences. */
  recurrenceConflicts: (input: RecurrenceConflictRequest | null) =>
    ["appointments", "recurrence-conflicts", input] as const,
};
